import clsx from 'clsx'
import { html, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { t } from '@/localization/localization'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './skeleton.scss?inline'

export type SkeletonVariant = 'text' | 'circular' | 'rectangular'
export type SkeletonAnimation = 'wave' | 'pulse' | 'custom'

/**
 * `dap-ds-skeleton`
 * @summary A skeleton loader component for displaying placeholder content while loading.
 * @element dap-ds-skeleton
 * @title - Skeleton
 *
 * @example Basic Usage
 * ```html
 * <dap-ds-skeleton></dap-ds-skeleton>
 * <dap-ds-skeleton variant="circular"></dap-ds-skeleton>
 * <dap-ds-skeleton variant="rectangular" width="200px" height="100px"></dap-ds-skeleton>
 * <dap-ds-skeleton animation="custom" custom-keyframes="0% { transform: scale(1); } 50% { transform: scale(1.1); } 100% { transform: scale(1); }"></dap-ds-skeleton>
 * ```
 *
 * @csspart base - The main skeleton container.
 *
 * @cssprop --dds-skeleton-color - The base color of the skeleton.
 * @cssprop --dds-skeleton-animation-duration - Duration of the loading animation.
 * @cssprop --dds-skeleton-border-radius - Border radius for rectangular skeletons.
 * @cssprop --dds-skeleton-text-spacing - Spacing between text lines in text variant.
 * @cssprop --dds-skeleton-animation-timing-function - Timing function for the loading animation.
 */
@customElement('dap-ds-skeleton')
export default class DapDSSkeleton extends DdsElement {
  /**
   * The variant of the skeleton.
   * @type {"text" | "circular" | "rectangular"}
   */
  @property({ type: String, reflect: true }) variant: SkeletonVariant = 'text'

  /**
   * The width of the skeleton. Can be any valid CSS width value.
   */
  @property({ type: String }) width?: string

  /**
   * The height of the skeleton. Can be any valid CSS height value.
   */
  @property({ type: String }) height?: string

  /**
   * Whether to animate the skeleton.
   */
  @property({ type: Boolean, reflect: true }) animated = true

  /**
   * The animation type for the skeleton.
   * @type {"wave" | "pulse" | "custom"}
   */
  @property({ type: String, reflect: true }) animation: SkeletonAnimation =
    'wave'

  /**
   * Custom keyframes for the animation when animation="custom".
   * Should be a valid CSS keyframes string without the @keyframes wrapper.
   * @example "0% { transform: scale(1); } 50% { transform: scale(1.1); } 100% { transform: scale(1); }"
   */
  @property({ type: String, attribute: 'custom-keyframes' })
  customKeyframes?: string

  static readonly styles = unsafeCSS(styles)

  private _customStyleSheet?: CSSStyleSheet

  private _updateCustomAnimation() {
    if (this.animation === 'custom' && this.customKeyframes) {
      // Create or update custom stylesheet
      const keyframesCSS = `
        @keyframes custom-skeleton-animation {
          ${this.customKeyframes}
        }
        .skeleton--custom::before {
          animation: custom-skeleton-animation var(--dds-skeleton-animation-duration) var(--dds-skeleton-animation-timing-function) infinite;
        }
      `

      if (this._customStyleSheet) {
        this._customStyleSheet.replaceSync(keyframesCSS)
      } else {
        this._customStyleSheet = new CSSStyleSheet()
        this._customStyleSheet.replaceSync(keyframesCSS)
        this.shadowRoot!.adoptedStyleSheets = [
          ...this.shadowRoot!.adoptedStyleSheets,
          this._customStyleSheet,
        ]
      }
    } else if (this._customStyleSheet) {
      // Remove custom stylesheet if not needed
      this.shadowRoot!.adoptedStyleSheets =
        this.shadowRoot!.adoptedStyleSheets.filter(
          sheet => sheet !== this._customStyleSheet,
        )
      this._customStyleSheet = undefined
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    if (
      changedProperties.has('animation') ||
      changedProperties.has('customKeyframes')
    ) {
      this._updateCustomAnimation()
    }
  }

  render() {
    if (this.width) {
      this.style.setProperty('--skeleton-width', this.width)
    } else {
      this.style.removeProperty('--skeleton-width')
    }

    if (this.height) {
      this.style.setProperty('--skeleton-height', this.height)
    } else {
      this.style.removeProperty('--skeleton-height')
    }

    this._updateCustomAnimation()

    return html`
      <div
        part="base"
        class="${clsx('skeleton', {
          [`skeleton--${this.variant}`]: this.variant,
          'skeleton--animate': this.animated,
          [`skeleton--${this.animation}`]: this.animated && this.animation,
        })}"
        role="status"
        aria-label=${this.ariaLabel ?? t('loading')}></div>
    `
  }
}
